import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref } from "vue"

import { computed, onBeforeMount, onMounted, Raw, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'
import { Sentry } from '@/services/sentry'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import RootPageLayout from '@/app/components/RootPageLayout.vue'
import { backend } from '@/services/backend'
import {
  BookOfTheDayView,
  BooksView,
  showReviewPrompt,
  trustpilotReviewPrompt,
} from '@/models/doc.book'
import { ArticlesView } from '@/models/doc.article'

import ContinueReadingBooksBlock from '@/app/components/listing/ContinueReadingBooksBlock.vue'
import NewReleasesBooksBlock from '@/app/components/listing/NewReleasesBooksBlock.vue'
import FreeBooksBlock from '@/app/components/listing/FreeBooksBlock.vue'
import BooksBlock from '@/app/components/listing/BooksBlock.vue'
import ArticlesBlock from '@/app/components/listing/ArticlesBlock.vue'
import { DocSortOption } from '@/models/doc'
import { auth } from '@/services/auth'
import { CollectionsView } from '@/models/docs.list.collection'
import CollectionsBlock from '@/app/components/collections/CollectionsBlock.vue'
import BookOfTheDayBlock from '@/app/components/listing/BookOfTheDayBlock.vue'
import { ExcerptView } from '@/models/excerpt'
import ExcerptBlock from '@/app/components/excerpt/ExcerptBlock.vue'
import { ListView } from '@/models/docs.list'
import ReadingListCallout16167 from '@/app/components/onboarding/ReadingListCallout16167.vue'
import { streaks16464Experiment, showNewOnboardingItems } from '@/services/ab'
import ReadingStreakWidget16464 from '@/app/components/streaks/ReadingStreakWidget16464.vue'
import { ReadingStreakData16464 } from '@/models/interfaces'
import ReadingStreakWidgetSkeleton16464 from '@/app/components/streaks/ReadingStreakWidgetSkeleton16464.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Discover',
  setup(__props) {

const router = useRouter()
const wait = useWait()

const articlesView = ref<Raw<ListView> | null>(null)
const freeBooksView = ref<Raw<ListView> | null>(null)
const newReleasesView = ref<Raw<ListView> | null>(null)
const continueReadingView = ref<Raw<ListView> | null>(null)
const popularBooksView = ref<Raw<ListView> | null>(null)
const recommendedBooksView = ref<Raw<ListView> | null>(null)
const collectionsView = ref<Raw<ListView> | null>(null)
const bookOfTheDayView = ref<Raw<BookOfTheDayView> | null>(null)
const excerptView = ref<ExcerptView | null>(null)
const readingStreak16464 = ref<ReadingStreakData16464 | null>(null)

const isFreeUser = ref(false)
const failedBlocks = ref(0)
const showReadingStreak16464 = ref(false)

onBeforeMount(async () => {
  isFreeUser.value = auth.isFreePlan()

  wait.start('Loading Discover')

  const streaksVariant = await streaks16464Experiment()
  showReadingStreak16464.value = streaksVariant === 'B'

  if (showReadingStreak16464.value) {
    // We deliberately don't await this function,
    // so that it doesn't block the loading of the page.
    getReadingStreak16464()
  }

  await getBooksAndArticles()
})

onMounted(async () => {
  const urlQueryParams = router.currentRoute.value.query

  // Check if we need to show the App Review Prompt
  // The trigger is going back to Home from reading a book. (#2812)
  if (urlQueryParams.from === 'book.page') {
    await showReviewPrompt()
    await trustpilotReviewPrompt()
  }
})

async function getReadingStreak16464(): Promise<void> {
  try {
    readingStreak16464.value = await backend.getReadingStreak16464()
  } catch (error: unknown) {
    Sentry.captureException(error)
  }
}

async function getBooksAndArticles(): Promise<void> {
  wait.start('Loading Discover')

  const blocksSetupFunctions = [
    setupBooks,
    setupArticles,
    setupBookCollections,
    setupExcerpt,
  ]

  const setupPromises = blocksSetupFunctions.map((setupFunction) =>
    setupFunction(),
  )
  const setupResults = await Promise.allSettled(setupPromises)
  setupResults.forEach((result) => {
    if (result.status === 'rejected') {
      failedBlocks.value++
    }
  })

  wait.end('Loading Discover')

  if (failedBlocks.value >= blocksSetupFunctions.length) {
    // If all blocks fail, display an error
    wait.start('Error Loading Discover')
  }

  // Load Book of the Day independently after page loads (non-blocking)
  setupBookOfTheDay()
}

async function setupBooks(): Promise<void> {
  const [booksData, userDocs] = await Promise.all([
    backend.getBooksWithRecommendations(),
    backend.getUserDocs(),
  ])

  if (booksData) {
    const showCtaBlock = false
    const showFreeAtTheTop = false

    const reading = await BooksView.createView(
      {
        data: userDocs.filter((doc) => doc.doc_type === 'book'),
      },
      undefined, // The defaultSort
      showCtaBlock,
      showFreeAtTheTop,
    )
    reading.enableContinueReading()
    reading.enableActionsButton()
    reading.setList('reading')
    reading.sort(DocSortOption.accessed)
    continueReadingView.value = new ListView('Continue Reading', reading, {
      viewAllLink: '/app/books/list/reading',
      customCardComponent: 'ContinueReadingCard',
    })

    const free = await BooksView.createView(
      booksData,
      DocSortOption.first_published_at,
      showCtaBlock,
      showFreeAtTheTop,
    )
    free.filterByFree()
    freeBooksView.value = new ListView('Free Books', free, {
      customCardComponent: 'NewReleasesCard',
    })

    const newReleases = await BooksView.createView(
      booksData,
      DocSortOption.first_published_at,
      showCtaBlock,
      showFreeAtTheTop,
    )
    newReleasesView.value = new ListView('New Releases', newReleases, {
      viewAllLink: '/app/books/list/new',
      customCardComponent: 'NewReleasesCard',
    })

    const popularBooks = await BooksView.createView(
      booksData,
      DocSortOption.popular,
      showCtaBlock,
      showFreeAtTheTop,
    )
    popularBooksView.value = new ListView('Popular Books', popularBooks, {
      viewAllLink: '/app/books/list/popular',
    })

    const recommendedBooks = await BooksView.createView(
      booksData,
      DocSortOption.recommended,
      false, // The showCtaBlock
      false, // The showFreeAtTheTop
    )
    recommendedBooksView.value = new ListView('Recommended', recommendedBooks, {
      viewAllLink: '/app/books/list/recommended',
    })
  }
}

async function setupArticles(): Promise<void> {
  const articlesData = await backend.getHomeArticles()
  if (articlesData) {
    const articles = await ArticlesView.createView(articlesData)
    articlesView.value = new ListView('Articles', articles, {
      viewAllLink: '/app/articles/list/all',
    })
  }
}

async function setupBookCollections(): Promise<void> {
  const collectionsData = await backend.getBookCollections()
  if (collectionsData) {
    const collections = new CollectionsView(collectionsData)
    collectionsView.value = new ListView('Collections', collections, {
      viewAllLink: '/app/collections',
    })
  }
}

async function setupExcerpt(): Promise<void> {
  const excerptData = await backend.getExcerpt()
  if (excerptData) {
    excerptView.value = new ExcerptView(excerptData)
  }
}

async function setupBookOfTheDay(): Promise<void> {
  try {
    const bookOfTheDayData = await backend.getBookOfTheDay()
    if (bookOfTheDayData) {
      bookOfTheDayView.value =
        await BookOfTheDayView.createView(bookOfTheDayData)
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

const hasRecommendations = computed(() => {
  const view = recommendedBooksView.value?.docs() as BooksView | undefined
  return !!view?.hasRecommendations
})

return (_ctx: any,_cache: any) => {
  const _component_LoadingError = _resolveComponent("LoadingError")!
  const _component_v_wait = _resolveComponent("v-wait")!

  return (_openBlock(), _createBlock(RootPageLayout, {
    class: "discover-page",
    title: "Discover the world's best ideas",
    mobileTitle: "Discover"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_wait, { for: "Error Loading Discover" }, {
        waiting: _withCtx(() => [
          _createVNode(_component_LoadingError)
        ]),
        _: 1
      }),
      _createVNode(_component_v_wait, {
        class: "layout__wrapper-inner container",
        for: "Loading Discover"
      }, {
        waiting: _withCtx(() => [
          _createVNode(LoadingSpinner)
        ]),
        default: _withCtx(() => [
          (showReadingStreak16464.value && readingStreak16464.value)
            ? (_openBlock(), _createBlock(ReadingStreakWidget16464, {
                key: 0,
                class: "discover-page__reading-streak",
                streakData: readingStreak16464.value
              }, null, 8, ["streakData"]))
            : (showReadingStreak16464.value)
              ? (_openBlock(), _createBlock(ReadingStreakWidgetSkeleton16464, {
                  key: 1,
                  class: "discover-page__reading-streak"
                }))
              : _createCommentVNode("", true),
          (continueReadingView.value)
            ? (_openBlock(), _createBlock(ContinueReadingBooksBlock, {
                key: 2,
                view: continueReadingView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (freeBooksView.value && freeBooksView.value.dataLength && isFreeUser.value)
            ? (_openBlock(), _createBlock(FreeBooksBlock, {
                key: 3,
                view: freeBooksView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (_unref(showNewOnboardingItems)())
            ? (_openBlock(), _createBlock(ReadingListCallout16167, { key: 4 }))
            : _createCommentVNode("", true),
          (excerptView.value)
            ? (_openBlock(), _createBlock(ExcerptBlock, {
                key: 5,
                view: excerptView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (recommendedBooksView.value && hasRecommendations.value)
            ? (_openBlock(), _createBlock(BooksBlock, {
                key: 6,
                view: recommendedBooksView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (newReleasesView.value)
            ? (_openBlock(), _createBlock(NewReleasesBooksBlock, {
                key: 7,
                view: newReleasesView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (popularBooksView.value)
            ? (_openBlock(), _createBlock(BooksBlock, {
                key: 8,
                view: popularBooksView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (collectionsView.value)
            ? (_openBlock(), _createBlock(CollectionsBlock, {
                key: 9,
                view: collectionsView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (bookOfTheDayView.value)
            ? (_openBlock(), _createBlock(BookOfTheDayBlock, {
                key: 10,
                view: bookOfTheDayView.value,
                title: "Book of the Day"
              }, null, 8, ["view"]))
            : _createCommentVNode("", true),
          (articlesView.value)
            ? (_openBlock(), _createBlock(ArticlesBlock, {
                key: 11,
                view: articlesView.value
              }, null, 8, ["view"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})